import React, { useState } from "react";
import { useAppSelector, useAppDispatch } from "../../../../store";
import { addWeaponMount, removeWeaponMount } from "../../../../store/features/shipBuilderSlice";
import { WEAPON_TYPES, AMMO_TYPES } from "../../../../constants/ship";
import { WeaponMount, Weapon, WeaponRange } from "../../../../types/weapons";
import { formatCurrency } from "../../../../utils/formatting";
import { calculateSpinalWeaponStats, validateSpinalWeaponInstallation } from "../../../../utils/calculations/weaponCalculations";
import {
  calculateSectionWeaponCapacity,
  validateSectionWeaponInstallation,
  canWeaponBeMounted
} from "../../../../utils/calculations/breakawayWeaponCalculations";
import { selectors } from "../../../../store/selectors/shipBuilderSelectors";
import WeaponSelector from "./WeaponSelector";
import { 
  calculateFirmpoints, 
  calculateHardpoints, 
  usesFirmpoints,
  getMountConfig,
  getFirmpointRangeLimit,
  calculateFirmpointPowerReduction
} from "../../../../utils/weaponMountCalculations";

interface BulkWeaponSystem {
  id: string;
  mountType: string;
  turretType?: string;
  weaponTypes: string[]; // Array of weapon types for multi-weapon turrets
  ammoTypes: (string | undefined)[]; // Array of ammo types corresponding to weapons
  quantity: number;
  hardpointsUsed: number;
  totalCost: number;
  totalTons: number;
  totalPower: number;
  weaponNames: string[]; // Array of weapon names
  weaponDamages: string[]; // Array of weapon damages
  weaponRanges: string[]; // Array of weapon ranges
  weaponTraits: string[][]; // Array of weapon traits arrays
}

export const BreakawayWeaponAssignmentInline: React.FC = () => {
  const dispatch = useAppDispatch();
  const shipTechLevel = useAppSelector((state) => state.shipBuilder.techLevel);
  const weaponMounts = useAppSelector((state) => state.shipBuilder.weaponMounts) || [];
  const breakawayHull = useAppSelector((state) => state.breakawayHull);
  const weightOverrideActive = useAppSelector(selectors.selectWeightOverrideActive);

  const [selectedSection, setSelectedSection] = useState<string>("");
  const [selectedMountType, setSelectedMountType] = useState<string>("");
  const [selectedTurretType, setSelectedTurretType] = useState<string>("single");
  const [selectedWeaponTypes, setSelectedWeaponTypes] = useState<string[]>([""]);
  const [selectedAmmoTypes, setSelectedAmmoTypes] = useState<(string | undefined)[]>([undefined]);
  const [quantity, setQuantity] = useState<number>(1);
  const [errors, setErrors] = useState<string>("");

  // Get available weapons based on mount type and tech level
  const getAvailableWeapons = (mountType: string) => {
    return Object.entries(WEAPON_TYPES).filter(([id, weapon]) => {
      // Check tech level requirement
      if (weapon.techLevel > shipTechLevel) {
        return false;
      }

      // Check mount type compatibility using utility function
      return canWeaponBeMounted(weapon, mountType);
    });
  };

  // Update weapon arrays when turret type changes
  const updateWeaponArrays = (turretType: string) => {
    const weaponCount = turretType === "single" ? 1 : turretType === "double" ? 2 : 3;
    setSelectedWeaponTypes(new Array(weaponCount).fill(""));
    setSelectedAmmoTypes(new Array(weaponCount).fill(undefined));
  };

  const getMountConfig = (mountType: string, turretType?: string) => {
    switch (mountType) {
      case "fixed":
        return { hardpointsUsed: 1, tons: 0, cost: 100000, maxWeapons: 3 }; // Traveller rules
      case "turret":
        const maxWeapons =
          turretType === "single" ? 1 : turretType === "double" ? 2 : 3;
        // Cost varies by turret type: Single MCr0.2, Double MCr0.5, Triple MCr1
        const turretCost = 
          turretType === "single" ? 200000 : // MCr0.2
          turretType === "double" ? 500000 : // MCr0.5
          1000000; // MCr1 for triple
        return { hardpointsUsed: 1, tons: 1, cost: turretCost, maxWeapons }; // Traveller rules
      case "barbette":
        return { hardpointsUsed: 1, tons: 5, cost: 0, maxWeapons: 1 }; // Traveller rules
      case "smallBay":
        return { hardpointsUsed: 1, tons: 50, cost: 0, maxWeapons: 1 }; // Traveller rules
      case "mediumBay":
        return { hardpointsUsed: 1, tons: 100, cost: 0, maxWeapons: 1 }; // Traveller rules
      case "largeBay":
        return { hardpointsUsed: 5, tons: 500, cost: 0, maxWeapons: 1 }; // Traveller rules
      case "spinalMount":
        // Spinal weapons use hardpoints equal to their tonnage divided by 100, rounding up
        // We'll calculate this properly when a weapon is added
        return { hardpointsUsed: 0, tons: 0, cost: 0, maxWeapons: 1 };
      default:
        return { hardpointsUsed: 1, tons: 1, cost: 100000, maxWeapons: 1 };
    }
  };

  const calculateSystemStats = (): BulkWeaponSystem | null => {
    if (!selectedMountType || selectedWeaponTypes.some(w => !w)) return null;

    const mountConfig = getMountConfig(selectedMountType, selectedTurretType);
    
    // Get weapon data for all selected weapons
    const weaponDataArray = selectedWeaponTypes.map(weaponType => 
      WEAPON_TYPES[weaponType as keyof typeof WEAPON_TYPES]
    ).filter(Boolean);
    
    if (weaponDataArray.length !== selectedWeaponTypes.length) return null;

    // Calculate totals for all weapons
    let totalWeaponCost = 0;
    let totalWeaponTons = 0;
    let totalWeaponPower = 0;
    const weaponNames: string[] = [];
    const weaponDamages: string[] = [];
    const weaponRanges: string[] = [];
    const weaponTraits: string[][] = [];

    weaponDataArray.forEach((weaponData, index) => {
      const weaponCost = weaponData.cost * 1000000; // Convert MCr to Cr
      const weaponTons = weaponData.weight;
      const weaponPower = weaponData.power;

      totalWeaponCost += weaponCost;
      totalWeaponTons += weaponTons;
      totalWeaponPower += weaponPower;
      weaponNames.push(weaponData.name);
      weaponDamages.push(weaponData.damage);
      weaponRanges.push(weaponData.range);
      weaponTraits.push(weaponData.traits);
    });

    const totalMountCost = mountConfig.cost * quantity;
    const totalWeaponCostTotal = totalWeaponCost * quantity;
    const totalMountTons = mountConfig.tons * quantity;
    const totalWeaponTonsTotal = totalWeaponTons * quantity;
    const totalMountPower = 0; // Mounts don't use power
    const totalWeaponPowerTotal = totalWeaponPower * quantity;
    const totalHardpointsUsed = mountConfig.hardpointsUsed * quantity;

    return {
      id: `${selectedMountType}-${selectedWeaponTypes.join('-')}-${Date.now()}`,
      mountType: selectedMountType,
      turretType: selectedTurretType,
      weaponTypes: selectedWeaponTypes,
      ammoTypes: selectedAmmoTypes,
      quantity,
      hardpointsUsed: totalHardpointsUsed,
      totalCost: totalMountCost + totalWeaponCostTotal,
      totalTons: totalMountTons + totalWeaponTonsTotal,
      totalPower: totalMountPower + totalWeaponPowerTotal,
      weaponNames,
      weaponDamages,
      weaponRanges,
      weaponTraits,
    };
  };

  const canAddSystem = () => {
    if (!selectedSection || !selectedMountType || selectedWeaponTypes.some(w => !w) || quantity <= 0)
      return false;

    const section = breakawayHull?.sections?.find(s => s.id === selectedSection);
    if (!section) return false;

    const { hardpoints, firmpoints } = calculateSectionWeaponCapacity(section.size);
    const mountConfig = getMountConfig(selectedMountType, selectedTurretType);
    const totalHardpointsNeeded = mountConfig.hardpointsUsed * quantity;

    if (selectedMountType === "fixed") {
      return totalHardpointsNeeded <= firmpoints;
    } else {
      return totalHardpointsNeeded <= hardpoints;
    }
  };

  const handleAddBulkSystem = () => {
    const systemStats = calculateSystemStats();
    if (!systemStats || !canAddSystem()) return;

    const section = breakawayHull?.sections?.find(s => s.id === selectedSection);
    if (!section) return;

    // Validate installation for breakaway sections
    const validationErrors = validateSectionWeaponInstallation(
      section.size,
      weaponMounts.filter(mount => (mount as any).sectionId === selectedSection),
      selectedMountType,
      quantity
    );

    if (validationErrors.length > 0) {
      setErrors(validationErrors.join(", "));
      return;
    }

    // Validate spinal weapon installation if applicable
    if (selectedMountType === "spinalMount" && selectedWeaponTypes[0]) {
      const weaponData = WEAPON_TYPES[selectedWeaponTypes[0] as keyof typeof WEAPON_TYPES];
      if (weaponData) {
        const validationErrors = validateSpinalWeaponInstallation(
          weaponData,
          section.size,
          1,
          weightOverrideActive
        );
        if (validationErrors.length > 0) {
          setErrors(`Cannot install spinal weapon: ${validationErrors.join(", ")}`);
          return;
        }
      }
    }

    // Create multiple identical mounts
    for (let i = 0; i < quantity; i++) {
      const mountConfig = getMountConfig(selectedMountType, selectedTurretType);
      
      // Create weapons array with different weapons for each slot
      const weapons: Weapon[] = [];
      
      selectedWeaponTypes.forEach((weaponType, index) => {
        const weaponData = WEAPON_TYPES[weaponType as keyof typeof WEAPON_TYPES];
        if (!weaponData) return;

        let ammoInfo = undefined;
        if ((weaponData as any).ammo && selectedAmmoTypes[index]) {
          ammoInfo = AMMO_TYPES[selectedAmmoTypes[index] as keyof typeof AMMO_TYPES];
        }

        const weapon: Weapon = {
          name: weaponData.name,
          description: weaponData.description,
          tl: weaponData.techLevel,
          range: weaponData.range as WeaponRange,
          power: weaponData.power,
          damage: weaponData.damage,
          cost: weaponData.cost * 1000000,
          tons: weaponData.weight,
          traits: weaponData.traits,
          mountType: weaponData.mountType,
          damageMultiple: (weaponData as any).damageMultiple,
          attackModifier: (weaponData as any).attackModifier,
          baseSize: (weaponData as any).baseSize,
          maxSize: (weaponData as any).maxSize,
          ammo: ammoInfo
            ? {
                type: ammoInfo.type,
                capacity: (weaponData as any).ammo.capacity,
                tonsPerReload: ammoInfo.tonsPerReload,
                costPerReload: ammoInfo.cost,
                name: ammoInfo.name,
                traits: ammoInfo.traits,
                damage: ammoInfo.damage,
                perReload: ammoInfo.perReload,
                tl: ammoInfo.tl,
              }
            : (weaponData as any).ammo,
          currentAmmo: ammoInfo ? (weaponData as any).ammo.capacity : undefined,
          selectedAmmoType: selectedAmmoTypes[index],
        };

        weapons.push(weapon);
      });

      // Calculate total power and tons for all weapons
      const totalWeaponPower = weapons.reduce((sum, weapon) => sum + weapon.power, 0);
      const totalWeaponTons = weapons.reduce((sum, weapon) => sum + weapon.tons, 0);
      const totalWeaponCost = weapons.reduce((sum, weapon) => sum + weapon.cost, 0);

      // For spinal weapons, calculate proper stats including hardpoints
      let mount: WeaponMount & { sectionId?: string; sectionName?: string };
      if (selectedMountType === "spinalMount") {
        const spinalStats = calculateSpinalWeaponStats(weapons[0], 1); // Default to 1x size
        mount = {
          type: selectedMountType,
          turretType: selectedTurretType,
          weapons: weapons,
          hardpointsUsed: spinalStats.hardpoints,
          range: "Close",
          power: spinalStats.power,
          tons: spinalStats.tons,
          cost: spinalStats.cost,
          maxWeapons: mountConfig.maxWeapons,
          sectionId: selectedSection,
          sectionName: section.name,
        };
      } else {
        mount = {
          type: selectedMountType,
          turretType: selectedTurretType,
          weapons: weapons,
          hardpointsUsed: mountConfig.hardpointsUsed,
          range: "Close",
          power: totalWeaponPower,
          tons: mountConfig.tons + totalWeaponTons,
          cost: mountConfig.cost + totalWeaponCost,
          maxWeapons: mountConfig.maxWeapons,
          sectionId: selectedSection,
          sectionName: section.name,
        };
      }

      dispatch(addWeaponMount(mount));
    }

    // Reset form
    setSelectedSection("");
    setSelectedMountType("");
    setSelectedTurretType("single");
    setSelectedWeaponTypes([""]);
    setSelectedAmmoTypes([undefined]);
    setQuantity(1);
    setErrors("");
  };

  const getMountTypeDisplay = (type: string) => {
    const typeMap: Record<string, string> = {
      turret: "Turret",
      barbette: "Barbette",
      smallBay: "Small Bay",
      mediumBay: "Medium Bay",
      largeBay: "Large Bay",
      spinalMount: "Spinal Mount",
      pointDefense: "Point Defense",
      fixed: "Fixed",
    };
    return typeMap[type] || type;
  };

  const systemStats = calculateSystemStats();

  // Get section stats for display
  const getSectionStats = () => {
    if (!selectedSection) return { hardpoints: 0, firmpoints: 0, hardpointsUsed: 0, firmpointsUsed: 0 };
    
    const section = breakawayHull?.sections?.find(s => s.id === selectedSection);
    if (!section) return { hardpoints: 0, firmpoints: 0, hardpointsUsed: 0, firmpointsUsed: 0 };

    const { hardpoints, firmpoints } = calculateSectionWeaponCapacity(section.size);
    const sectionMounts = weaponMounts.filter(mount => (mount as any).sectionId === selectedSection);
    const hardpointsUsed = sectionMounts.reduce((total, mount) => total + mount.hardpointsUsed, 0);
    const firmpointsUsed = sectionMounts.filter(mount => mount.type === "fixed").length;

    return { hardpoints, firmpoints, hardpointsUsed, firmpointsUsed };
  };

  const sectionStats = getSectionStats();
  const availableHardpoints = sectionStats.hardpoints - sectionStats.hardpointsUsed;
  const availableFirmpoints = sectionStats.firmpoints - sectionStats.firmpointsUsed;

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
      <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
        Weapon Assignment
      </h3>

      {/* Section Selection */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Target Section
        </label>
        <select
          value={selectedSection}
          onChange={(e) => setSelectedSection(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
        >
          <option value="">Choose a section...</option>
          {breakawayHull?.sections?.map((section) => (
            <option key={section.id} value={section.id}>
              {section.name} ({section.size} tons)
            </option>
          ))}
        </select>
      </div>

      {selectedSection && (
        <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-md">
          <p className="text-sm text-blue-800 dark:text-blue-200">
            <span className="font-medium">Hardpoints Available:</span>{" "}
            {availableHardpoints} / {sectionStats.hardpoints}
            {sectionStats.firmpoints > 0 && (
              <span>
                {" "}
                | <span className="font-medium">Firmpoints Available:</span>{" "}
                {availableFirmpoints} / {sectionStats.firmpoints}
              </span>
            )}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {/* Mount Type Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Mount Type
          </label>
          <select
            value={selectedMountType}
            onChange={(e) => {
              setSelectedMountType(e.target.value);
              setSelectedWeaponTypes([""]);
              setSelectedAmmoTypes([undefined]);
            }}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
          >
            <option value="">Choose mount type...</option>
            {sectionStats.firmpoints > 0 && <option value="fixed">Fixed Mount</option>}
            <option value="turret">Turret</option>
            <option value="barbette">Barbette</option>
            <option value="smallBay">Small Bay</option>
            <option value="mediumBay">Medium Bay</option>
            <option value="largeBay">Large Bay</option>
            <option value="spinalMount">Spinal Mount</option>
          </select>
        </div>

        {/* Turret Type (if turret selected) */}
        {selectedMountType === "turret" && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Turret Type
            </label>
            <select
              value={selectedTurretType}
              onChange={(e) => {
                setSelectedTurretType(e.target.value);
                updateWeaponArrays(e.target.value);
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
            >
              <option value="single">Single (1 weapon)</option>
              <option value="double">Double (2 weapons)</option>
              <option value="triple">Triple (3 weapons)</option>
            </select>
          </div>
        )}

        {/* Quantity Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Quantity
          </label>
          <div className="flex items-center border border-gray-300 dark:border-gray-600 rounded-md">
            <button
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              disabled={quantity <= 1}
              className="px-3 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              -
            </button>
            <input
              type="number"
              min="1"
              step="1"
              value={quantity}
              onChange={(e) =>
                setQuantity(Math.max(1, parseInt(e.target.value) || 1))
              }
              className="w-full text-center border-0 focus:ring-0 focus:outline-none dark:bg-gray-700 dark:text-gray-100"
            />
            <button
              onClick={() => setQuantity(quantity + 1)}
              className="px-3 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
            >
              +
            </button>
          </div>
        </div>
      </div>

      {/* Weapon Type Selection */}
      {selectedMountType && (
        <div className="col-span-full mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Weapon Types
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {selectedWeaponTypes.map((weaponType, index) => (
              <div key={index}>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Weapon {index + 1}
                </label>
                <select
                  value={weaponType}
                  onChange={(e) => {
                    const newWeaponTypes = [...selectedWeaponTypes];
                    newWeaponTypes[index] = e.target.value;
                    setSelectedWeaponTypes(newWeaponTypes);
                    
                    const newAmmoTypes = [...selectedAmmoTypes];
                    newAmmoTypes[index] = undefined;
                    setSelectedAmmoTypes(newAmmoTypes);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Choose weapon...</option>
                  {getAvailableWeapons(selectedMountType).map(([id, weapon]) => (
                    <option key={id} value={id}>
                      {weapon.name} (TL{weapon.techLevel}) - {weapon.damage}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Ammo Type Selection (if weapons use ammo) */}
      {selectedWeaponTypes.some(weaponType => 
        weaponType && WEAPON_TYPES[weaponType as keyof typeof WEAPON_TYPES]?.ammo
      ) && (
        <div className="col-span-full mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Ammunition Types
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {selectedWeaponTypes.map((weaponType, index) => {
              const weaponData = weaponType ? WEAPON_TYPES[weaponType as keyof typeof WEAPON_TYPES] : null;
              const needsAmmo = weaponData?.ammo;
              
              return (
                <div key={index}>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Ammo for Weapon {index + 1}
                  </label>
                  {needsAmmo ? (
                    <select
                      value={selectedAmmoTypes[index] || ""}
                      onChange={(e) => {
                        const newAmmoTypes = [...selectedAmmoTypes];
                        newAmmoTypes[index] = e.target.value || undefined;
                        setSelectedAmmoTypes(newAmmoTypes);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Choose ammo...</option>
                      {Object.entries(AMMO_TYPES)
                        .filter(([, ammo]) => weaponData.ammo && ammo.type === weaponData.ammo.type)
                        .map(([id, ammo]) => (
                          <option key={id} value={id}>
                            {ammo.name} (TL{ammo.tl}) - {ammo.damage}
                          </option>
                        ))}
                    </select>
                  ) : (
                    <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-500 text-sm">
                      No ammo required
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Error Display */}
      {errors && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-800">{errors}</p>
        </div>
      )}

      {/* System Preview */}
      {systemStats && (
        <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-md">
          <h4 className="text-sm font-medium text-gray-700 mb-3">
            System Preview
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-600">System:</span>
              <div className="text-gray-900">
                {quantity}x {getMountTypeDisplay(systemStats.mountType)}
                {systemStats.turretType &&
                  ` (${systemStats.turretType.charAt(0).toUpperCase() + systemStats.turretType.slice(1)})`}
              </div>
            </div>
            <div>
              <span className="font-medium text-gray-600">Weapon:</span>
              <div className="text-gray-900">{systemStats.weaponNames.join(", ")}</div>
            </div>
            <div>
              <span className="font-medium text-gray-600">Hardpoints:</span>
              <div className="text-gray-900">{systemStats.hardpointsUsed}</div>
            </div>
            <div>
              <span className="font-medium text-gray-600">Total Cost:</span>
              <div className="text-gray-900">
                {formatCurrency(systemStats.totalCost)}
              </div>
            </div>
            <div>
              <span className="font-medium text-gray-600">Total Weight:</span>
              <div className="text-gray-900">
                {systemStats.totalTons.toFixed(1)} tons
              </div>
            </div>
            <div>
              <span className="font-medium text-gray-600">Total Power:</span>
              <div className="text-gray-900">{systemStats.totalPower}</div>
            </div>
            <div>
              <span className="font-medium text-gray-600">Damage:</span>
              <div className="text-gray-900">{systemStats.weaponDamages.join(", ")}</div>
            </div>
            <div>
              <span className="font-medium text-gray-600">Range:</span>
              <div className="text-gray-900">{systemStats.weaponRanges.join(", ")}</div>
            </div>
          </div>
        </div>
      )}

      {/* Add Button */}
      <div className="flex justify-center">
        <button
          onClick={handleAddBulkSystem}
          disabled={!canAddSystem()}
          className={`px-6 py-3 rounded-md font-medium transition-colors ${
            canAddSystem()
              ? "bg-blue-600 text-white hover:bg-blue-700"
              : "bg-gray-300 text-gray-500 cursor-not-allowed"
          }`}
        >
          Add {quantity} {quantity === 1 ? "System" : "Systems"}
        </button>
      </div>

      {/* Installed Mounts Section */}
      {selectedSection && (
        <div className="mt-8">
          <h4 className="text-base font-semibold text-gray-800 mb-4">
            Installed Mounts - {breakawayHull?.sections?.find(s => s.id === selectedSection)?.name} ({weaponMounts.filter(mount => (mount as any).sectionId === selectedSection).length})
          </h4>
          {weaponMounts
            .filter(mount => (mount as any).sectionId === selectedSection)
            .map((mount, index) => {
              const originalIndex = weaponMounts.findIndex(m => m === mount);
              return (
                <WeaponSelector
                  key={originalIndex}
                  mountIndex={originalIndex}
                  mount={mount}
                  removeMount={() => dispatch(removeWeaponMount(originalIndex))}
                />
              );
            })}
          {weaponMounts.filter(mount => (mount as any).sectionId === selectedSection).length === 0 && (
            <p className="text-gray-500 text-center py-4">No weapon mounts configured for this section.</p>
          )}
        </div>
      )}
    </div>
  );
};
